import { getGlobalSearchItemNavigationIcon } from "@frontend-shared/utils";
import type { ParseKeys } from "i18next";
import type { LucideIcon } from "lucide-react";
import { Search } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NavigationIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { CommandItem } from "@/components/ui/command";
import { getAutoDirection } from "@/lib/direction";

import {
  GLOBAL_SEARCH_BADGE_KEYS,
  GLOBAL_SEARCH_ITEM_ICONS,
  GLOBAL_SEARCH_NAVIGATION_ICON_STYLE,
} from "./constants";
import type { IGlobalSearchItemRowProps } from "./types";

// NOTE: One result row, styled like the chat rows it sits beside. Titles are
// user content in any script, so each line picks its own direction.
const GlobalSearchItemRowComponent = ({
  item,
  onSelectItem,
}: IGlobalSearchItemRowProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const Icon: LucideIcon | undefined = GLOBAL_SEARCH_ITEM_ICONS[item.type];
  const ItemIcon = Icon ?? Search;
  const navigationIcon = getGlobalSearchItemNavigationIcon(item);
  const badgeKey: ParseKeys<"globalSearch"> | undefined =
    item.params.badge === undefined
      ? undefined
      : GLOBAL_SEARCH_BADGE_KEYS[item.params.badge];
  const itemKey = `${item.type}:${item.id}`;

  const handleSelect = useCallback((): void => {
    onSelectItem(item);
  }, [item, onSelectItem]);

  return (
    <CommandItem
      value={itemKey}
      onSelect={handleSelect}
      className="rounded-lg group py-0 px-2.5"
      data-testid={`global-search-item-${itemKey}`}
    >
      <div className="flex items-center gap-2 border-b group-last-of-type:border-b-0 grow py-2.5 min-w-0">
        <div className="p-2 border rounded-lg shrink-0 group-data-[selected=true]:border-transparent bg-icon-background group-data-[selected=true]:bg-transparent">
          {navigationIcon === undefined ? (
            <ItemIcon className="h-5 w-5 text-foreground" />
          ) : (
            <NavigationIcon
              icon={navigationIcon}
              stroke="currentColor"
              style={GLOBAL_SEARCH_NAVIGATION_ICON_STYLE}
              className="h-5 w-5 text-foreground"
            />
          )}
        </div>
        <div className="flex flex-col min-w-0 grow">
          <p
            dir={getAutoDirection({ content: item.title })}
            className="text-sm font-medium text-start group-data-[selected=true]:text-primary truncate"
          >
            {item.title}
          </p>
          {item.subtitle && (
            <p
              dir={getAutoDirection({ content: item.subtitle })}
              className="text-xs text-start text-foreground-secondary group-data-[selected=true]:text-primary truncate"
            >
              {item.subtitle}
            </p>
          )}
        </div>
        {badgeKey && (
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {t(badgeKey)}
          </Badge>
        )}
      </div>
    </CommandItem>
  );
};

export const GlobalSearchItemRow = memo(GlobalSearchItemRowComponent);
